<script setup lang="ts" generic="T extends KaRadioButtonOptionValueType">
import { RadioGroupRoot } from 'reka-ui';
import { computed, ref, useSlots } from 'vue';

import { KaMessage } from '@/kasten-components/components/KaMessage';
import type { KaVeeFormFieldEmitContract } from '@/kasten-components/components/KaVeeFormField';
import { useDataTestid } from '@/kasten-components/composables';
import type { VueClass, WithDefaults } from '@/kasten-components/types';
import { unwrapElement } from '@/kasten-components/utils';
import { cnMerge } from '@/utils/utils.js';

import KaRadioButtonGroupOption from './KaRadioButtonGroupOption.vue';
import type { KaRadioButtonGroupProps, KaRadioButtonOptionValueType } from './types';
export type {
  KaRadioButtonGroupProps,
  KaRadioButtonOptionProps,
  KaRadioButtonOptionValueType,
} from './types';

defineOptions({
  name: 'KaRadioButtonGroup',
  inheritAttrs: false,
});

const props = withDefaults(defineProps<KaRadioButtonGroupProps<T>>(), {
  modelValue: undefined,
  hideLabel: false,
  hideDescription: false,
  showErrorStylesOnly: false,
}) as WithDefaults<KaRadioButtonGroupProps<T>, 'hideLabel' | 'hideDescription'>;

const slots = useSlots();

// prettier-ignore
const emit = defineEmits< KaVeeFormFieldEmitContract<T> & { (e: 'touch'): void; (e: 'blur', event: FocusEvent): void; (e: 'update:modelValue', value: T): void; (e: 'change', value: T): void; } >();

const fieldSetRef = ref<HTMLElement>();

const internalValue = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('touch');
    emit('change', value!);
    emit('update:modelValue', value!);
  },
});

const id = computed(() => props.id ?? props.name);
const { dataTestid, suffix } = useDataTestid(computed(() => props.dataTestid ?? id.value));
const labelId = computed(() => `${id.value}-label`);
const isRequired = computed(() => props.required || props.ariaRequired);
const descriptionId = computed(() =>
  props.description || slots.description || isRequired.value
    ? `${id.value}-description`
    : undefined,
);
const fallbackErrorMessageId = computed(() => `${id.value}-error-message`);
const shouldDisplayDescription = computed(
  () => (props.description || slots.description) && !props.hideDescription,
);
const hasError = computed(() => props.errorMessage !== undefined);

const handleBlur = (event: FocusEvent) =>
  setTimeout(() => {
    if (shouldDispatchBlur(event)) {
      emit('blur', event);
    }
  });

// Decides whether blur event should be dispatched or not
const shouldDispatchBlur = (event: FocusEvent) => {
  const nextTarget = event.relatedTarget ?? document.activeElement;
  // Blur didn't result in focus on smth else
  if (nextTarget === null) return true;

  // If target is not an HTML Node - we can't react on it anyhow. Consider that this is blur.
  if (!(nextTarget instanceof Node)) return true;

  const fieldsetEl = unwrapElement(fieldSetRef);
  if (!fieldsetEl) return true;

  return !fieldsetEl.contains(nextTarget);
};

const computedErrorMessageId = computed(() => {
  if (props.showErrorStylesOnly) return props.errorMessageId || undefined;
  return hasError.value ? fallbackErrorMessageId.value : undefined;
});

// Combine description and error ids to announce both on group and item focus
const describedByIds = computed<string | undefined>(() => {
  const ids = [descriptionId.value, computedErrorMessageId.value].filter((v): v is string =>
    Boolean(v),
  );
  return ids.length ? ids.join(' ') : undefined;
});
</script>

<template>
  <RadioGroupRoot
    :id="id"
    ref="fieldSetRef"
    v-model="internalValue as any"
    :data-testid="dataTestid"
    :required="isRequired"
    :aria-labelledby="labelId"
    :aria-describedby="describedByIds"
    :aria-invalid="props.showErrorStylesOnly || hasError"
    :aria-errormessage="computedErrorMessageId"
    aria-orientation="horizontal"
    class="w-full"
  >
    <div
      :id="labelId"
      :data-testid="suffix('label')"
      :class="[
        'flex items-center text-sm font-semibold leading-6 text-gray-900 dark:text-gray-50',
        !shouldDisplayDescription ? 'mx-0 mb-3 mt-0' : 'm-0',
        { 'sr-only': props.hideLabel },
      ]"
    >
      <slot name="label" v-bind="{ label: props.label }">{{ props.label }}</slot>
      <span
        v-if="isRequired && !props.hideRequiredAsterisk"
        aria-hidden="true"
        :data-testid="suffix('required-asterisk')"
        >&nbsp;*</span
      >
    </div>

    <div
      v-if="props.description || slots.description || isRequired"
      :id="descriptionId"
      :data-testid="suffix('description')"
      :class="[
        'flex items-center text-sm text-gray-600 dark:text-gray-400',
        shouldDisplayDescription ? 'mx-0 mb-3 mt-0' : 'm-0',
        {
          'sr-only':
            props.hideDescription || (!props.description && !$slots.description && isRequired),
        },
      ]"
    >
      <slot name="description" v-bind="{ description: props.description }">
        {{ props.description }}
      </slot>
      <span v-if="isRequired" class="sr-only">
        {{ props.label || 'This' }} is a required field.
      </span>
    </div>

    <div
      :class="
        cnMerge(
          {
            'grid grid-cols-[repeat(auto-fit,_minmax(12rem,_1fr))] gap-2': !props.groupClasses,
          },
          props.groupClasses,
        )
      "
    >
      <KaRadioButtonGroupOption
        v-for="option in props.options"
        v-bind="
          // `as any` is not big deal here because options are already type checked in Props.
          // The problem is that this component has internal and external versions of Prop
          // to be compatible with current ability of vue compiler to interpret TS types with defineProps micro
          option as any
        "
        :key="option.id ?? option.value.toString()"
        :data-testid="suffix(`radio-option-${option.label}`)"
        :class="props.optionClasses"
        :aria-describedby="describedByIds"
        :size="props.size"
        @blur="handleBlur"
      />
    </div>

    <KaMessage
      v-if="!props.showErrorStylesOnly && hasError"
      :id="fallbackErrorMessageId"
      :data-testid="suffix('error-message')"
      :message="props.errorMessage"
      background="transparent"
      variant="error"
      :role="null"
      :class="['mt-2', { 'sr-only': props.hideErrorMessage }] as VueClass"
    />
  </RadioGroupRoot>
</template>
